/**
 * One bucket row of the Budget month view (Phase 3, §6.2): a `BucketChip`, an
 * inline de-DE amount input (empty = kein Budget), the month's `spent`, a
 * progress bar, and the remaining figure ("über Budget" when negative). The
 * input commits on blur/Enter and reverts on Escape; the parent owns the
 * optimistic PUT.
 */
import { useState } from 'react';
import { money } from '../../lib/format.ts';
import { AmountText } from '../../components/ui/AmountText.tsx';
import { BucketChip } from '../../components/ui/BucketChip.tsx';
import { Input } from '../../components/ui/Input.tsx';
import type { BucketOut, MonthBucketRow } from '../../lib/types.ts';
import { BudgetProgressBar } from './BudgetProgressBar.tsx';
import { budgetProgress, formatBudgetInput, parseBudgetInput } from './budgetMath.ts';

export interface BudgetRowProps {
  bucket: BucketOut;
  row: MonthBucketRow | undefined;
  disabled?: boolean;
  /** Bucket (or its group) is archived — rendered for historical months only. */
  archived?: boolean;
  /** Persist a new budget (`null` clears); the parent runs the optimistic PUT. */
  onSetBudget: (bucketId: number, amount: string | null) => void;
}

export function BudgetRow({ bucket, row, disabled, archived, onSetBudget }: BudgetRowProps) {
  const budgeted = row?.budgeted ?? null;
  const spentStr = row?.spent ?? '0.00';
  const spent = Number.parseFloat(spentStr) || 0;
  const budgetNum = budgeted !== null ? Number.parseFloat(budgeted) : null;
  const progress = budgetProgress(spent, budgetNum);

  const [draft, setDraft] = useState(() => formatBudgetInput(budgeted));
  const [editing, setEditing] = useState(false);
  const [invalid, setInvalid] = useState(false);

  // Reflect server/optimistic changes while the field is not being edited, using
  // the sanctioned "adjust state during render" pattern (no effect → no cascade).
  const [syncedBudgeted, setSyncedBudgeted] = useState(budgeted);
  if (!editing && budgeted !== syncedBudgeted) {
    setSyncedBudgeted(budgeted);
    setDraft(formatBudgetInput(budgeted));
  }

  const commit = () => {
    setEditing(false);
    const parsed = parseBudgetInput(draft);
    if (!parsed.valid) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if ((parsed.value ?? null) === (budgeted ?? null)) {
      setDraft(formatBudgetInput(budgeted));
      return;
    }
    onSetBudget(bucket.id, parsed.value);
  };

  return (
    <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <BucketChip bucket={bucket} size="sm" />
        {archived ? (
          <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Archiviert
          </span>
        ) : null}
        {row && row.txn_count > 0 ? (
          <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">
            {row.txn_count} {row.txn_count === 1 ? 'Buchung' : 'Buchungen'}
          </span>
        ) : null}
      </div>

      <div className="flex items-center gap-3 sm:w-auto">
        <div className="flex-1 sm:w-28">
          <div className="relative">
            <Input
              inputMode="decimal"
              aria-label={`Budget für ${bucket.name}`}
              placeholder="kein Budget"
              value={draft}
              invalid={invalid}
              disabled={disabled}
              className="pr-6 text-right tabular-nums"
              onChange={(e) => {
                setDraft(e.target.value);
                setInvalid(false);
              }}
              onFocus={() => setEditing(true)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                } else if (e.key === 'Escape') {
                  setEditing(false);
                  setInvalid(false);
                  setDraft(formatBudgetInput(budgeted));
                }
              }}
            />
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-slate-400 dark:text-slate-500"
            >
              €
            </span>
          </div>
        </div>

        <div className="w-20 shrink-0 text-right">
          {/* Signed actual (spent = -actual): net outflow stays negative/rose,
              a net inflow (income bucket, refund month) shows positive/emerald,
              and an idle bucket shows "0,00 €" instead of a forced "-0,00 €". */}
          <AmountText value={row?.actual ?? '0.00'} className="text-sm" />
        </div>
      </div>

      <div className="flex items-center gap-3 sm:w-64">
        <div className="flex-1">
          <BudgetProgressBar barPct={progress.barPct} band={progress.band} pct={progress.pct} />
        </div>
        <div className="w-24 shrink-0 text-right text-xs">
          {progress.remaining === null ? (
            <span className="text-slate-400 dark:text-slate-500">–</span>
          ) : progress.remaining < 0 ? (
            <span className="font-medium text-rose-600 dark:text-rose-400">
              {money(progress.remaining)} über
            </span>
          ) : (
            <span className="text-slate-500 dark:text-slate-400">
              {money(progress.remaining)} übrig
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
